import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as companyApi from '../Api/companyApi';

const COMPANY_KEY = 'companies';

/** Refresh company + settings queries after branding.logo changes */
export const invalidateCompanyBrandingCache = (queryClient, companyId) => {
	queryClient.invalidateQueries({ queryKey: [COMPANY_KEY] });
	if (companyId) {
		queryClient.invalidateQueries({ queryKey: [COMPANY_KEY, companyId] });
		queryClient.invalidateQueries({
			queryKey: [COMPANY_KEY, companyId, 'settings'],
		});
	}
};

/** Optimistically set branding.logo in company + settings query caches */
export const patchCompanyBrandingLogoInCache = (queryClient, companyId, logoUrl) => {
	if (!companyId) return;

	queryClient.setQueryData([COMPANY_KEY, companyId, 'settings'], (old) => {
		const data = old?.data || { settings: {} };
		return {
			...(old || {}),
			data: {
				...data,
				settings: {
					...(data.settings || {}),
					branding: {
						...(data.settings?.branding || {}),
						logo: logoUrl,
					},
				},
			},
		};
	});

	queryClient.setQueryData([COMPANY_KEY, companyId], (old) => {
		const data = old?.data || { companySettings: {} };
		return {
			...(old || {}),
			data: {
				...data,
				companySettings: {
					...(data.companySettings || {}),
					branding: {
						...(data.companySettings?.branding || {}),
						logo: logoUrl,
					},
				},
			},
		};
	});
};

export const useCompanies = () =>
	useQuery({
		queryKey: [COMPANY_KEY],
		queryFn: async () => {
			const result = await companyApi.getCompanies();
			// Extract the data from axios response (the API returns the array directly)
			return result.data || [];
		},
	});

export const useCompany = (id) =>
	useQuery({
		queryKey: [COMPANY_KEY, id],
		queryFn: () => companyApi.getCompanyById(id),
		enabled: !!id,
	});

export const useCreateCompany = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: companyApi.createCompany,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [COMPANY_KEY] });
		},
	});
};

export const useUpdateCompany = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, data }) => companyApi.updateCompany(id, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [COMPANY_KEY] });
		},
	});
};

export const useDeleteCompany = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id) => companyApi.deleteCompany(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [COMPANY_KEY] });
		},
	});
};

export const useCompanySettings = (companyId) =>
	useQuery({
		queryKey: [COMPANY_KEY, companyId, 'settings'],
		queryFn: () => companyApi.getCompanySettings(companyId),
		enabled: !!companyId,
		staleTime: 1000 * 60 * 5, // 5 minutes
	});

export const useUpdateCompanySettings = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ companyId, settings }) =>
			companyApi.updateCompanySettings(companyId, settings),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [COMPANY_KEY] });
		},
	});
};
